import { fetchWithAuth, BASE_URL } from './client';
import { logError } from '../../shared/utils/logger';

/**
 * Create a new API key
 * POST /api/developer/keys
 */
export async function createApiKey(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/developer/keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        logError('Failed to create API key', response.status);
        const error = new Error('Failed to create API key');
        error.status = response.status;
        error.statusText = response.statusText;
        throw error;
    }
    return response.json();
}

/**
 * List all user's API keys
 * GET /api/developer/keys
 */
export async function fetchApiKeys() {
    const response = await fetchWithAuth(`${BASE_URL}/api/developer/keys`);
    if (!response.ok) {
        logError('Failed to fetch API keys', response.status);
        throw new Error('Failed to fetch API keys');
    }
    return response.json();
}

/**
 * Get API key details
 * GET /api/developer/keys/{key_id}
 */
export async function fetchApiKeyDetails(keyId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/developer/keys/${keyId}`);
    if (!response.ok) {
        logError(`Failed to fetch API key ${keyId}`, response.status);
        throw new Error('Failed to fetch API key details');
    }
    return response.json();
}

/**
 * Update API key name/permissions
 * PATCH /api/developer/keys/{key_id}
 */
export async function updateApiKey(keyId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/developer/keys/${keyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    if (!response.ok) {
        logError(`Failed to update API key ${keyId}`, response.status);
        throw new Error('Failed to update API key');
    }
    return response.json();
}

/**
 * Delete/revoke API key
 * DELETE /api/developer/keys/{key_id}
 */
export async function deleteApiKey(keyId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/developer/keys/${keyId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError(`Failed to delete API key ${keyId}`, response.status);
        throw new Error('Failed to delete API key');
    }
    // Handle 204 No Content
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

/**
 * Rotate API key (generate new secret)
 * POST /api/developer/keys/{key_id}/rotate
 */
export async function rotateApiKey(keyId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/developer/keys/${keyId}/rotate`, {
        method: 'POST'
    });
    if (!response.ok) {
        logError(`Failed to rotate API key ${keyId}`, response.status);
        throw new Error('Failed to rotate API key');
    }
    return response.json();
}

/**
 * Get usage summary
 * GET /api/developer/usage
 */
export async function fetchUsageSummary() {
    const response = await fetchWithAuth(`${BASE_URL}/api/developer/usage`);
    if (!response.ok) {
        logError('Failed to fetch usage summary', response.status);
        throw new Error('Failed to fetch usage summary');
    }
    return response.json();
}

/**
 * Get daily usage breakdown
 * GET /api/developer/usage/daily
 */
export async function fetchDailyUsage() {
    const response = await fetchWithAuth(`${BASE_URL}/api/developer/usage/daily`);
    if (!response.ok) {
        logError('Failed to fetch daily usage', response.status);
        throw new Error('Failed to fetch daily usage');
    }
    return response.json();
}

/**
 * Get available permission scopes
 * GET /api/developer/permissions
 */
export async function fetchPermissionScopes() {
    const response = await fetchWithAuth(`${BASE_URL}/api/developer/permissions`);
    if (!response.ok) {
        logError('Failed to fetch permission scopes', response.status);
        throw new Error('Failed to fetch permission scopes');
    }
    return response.json();
}

/**
 * Get detailed request logs
 * GET /api/developer/usage/logs
 * @param {Object} params - Query parameters
 * @param {string} params.startDate - Start date (ISO string)
 * @param {string} params.endDate - End date (ISO string)
 * @param {string} params.status - Filter by status (success, error, all)
 * @param {string} params.endpoint - Filter by endpoint
 * @param {string} params.apiKeyId - Filter by API key ID
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 */
export async function fetchRequestLogs(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    if (params.status && params.status !== 'all') queryParams.append('status', params.status);
    if (params.endpoint) queryParams.append('endpoint', params.endpoint);
    if (params.apiKeyId) queryParams.append('api_key_id', params.apiKeyId);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `${BASE_URL}/api/developer/usage/logs${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
        logError('Failed to fetch request logs', response.status);
        throw new Error('Failed to fetch request logs');
    }
    return response.json();
}

/**
 * Get usage analytics/breakdown
 * GET /api/developer/usage/analytics
 * @param {Object} params - Query parameters
 * @param {string} params.startDate - Start date (ISO string)
 * @param {string} params.endDate - End date (ISO string)
 * @param {string} params.groupBy - Group by (endpoint, api_key, status, day, hour)
 */
export async function fetchUsageAnalytics(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    if (params.groupBy) queryParams.append('group_by', params.groupBy);

    const url = `${BASE_URL}/api/developer/usage/analytics${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
        logError('Failed to fetch usage analytics', response.status);
        throw new Error('Failed to fetch usage analytics');
    }
    return response.json();
}

/**
 * Export usage data as CSV
 * GET /api/developer/usage/export
 * @param {Object} params - Query parameters
 * @param {string} params.startDate - Start date (ISO string)
 * @param {string} params.endDate - End date (ISO string)
 * @param {string} params.format - Export format (csv, json)
 */
export async function exportUsageData(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    if (params.format) queryParams.append('format', params.format);

    const url = `${BASE_URL}/api/developer/usage/export${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
        logError('Failed to export usage data', response.status);
        throw new Error('Failed to export usage data');
    }

    // For CSV, return blob; for JSON, return parsed JSON
    if (params.format === 'csv') {
        return response.blob();
    }
    return response.json();
}
